// One-prop debug bypass.
//
// Activation: `localStorage.noSignIn = "debug"` then reload. The AuthProvider
// short-circuits the Firebase auth listener, WorkspaceProvider seeds a synthetic
// personal workspace, and TimelineProvider seeds elements from src/data/mockData.
// Clear (or set to anything other than "debug") and reload to restore the normal
// Google sign-in flow.
//
// Toggle key: keep `NO_SIGN_IN_BYPASS_VALUE` in lockstep with the literal in
// AuthContext — that file is the historic owner of the flag and other modules
// import the predicate from here.
//
// Identity switcher: when bypass is active, `localStorage.noSignInRole` selects
// which fixture identity is "signed in" — admin / editor / viewer / helper /
// helper2 — so the floating <DebugRoleSwitcher /> can hot-swap roles and walk
// the four-role UI matrix without re-logging.

export const NO_SIGN_IN_STORAGE_KEY = 'noSignIn'
export const NO_SIGN_IN_BYPASS_VALUE = 'debug'
export const NO_SIGN_IN_MOCK_VALUE = 'mock'
export const NO_SIGN_IN_ROLE_STORAGE_KEY = 'noSignInRole'
export const ACTIVE_WORKSPACE_STORAGE_KEY = 'timelining-active-workspace'
export const APP_MODE_STORAGE_KEY = 'timelining-app-mode'
const DEBUG_WRAPPER_ROLE_STORAGE_KEY = 'timelining-debug-wrapper-role'

export const purgeObsoletePreviewStorage = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(NO_SIGN_IN_STORAGE_KEY)
    window.localStorage.removeItem(NO_SIGN_IN_ROLE_STORAGE_KEY)
    window.localStorage.removeItem(DEBUG_WRAPPER_ROLE_STORAGE_KEY)
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * Strips obsolete preview bypass query parameters from the URL and ensures
 * legacy localStorage keys are purged so they cannot taint live SIT sessions.
 */
export const activateDebugBypassFromUrl = (): void => {
  if (typeof window === 'undefined') return
  purgeObsoletePreviewStorage()

  try {
    const url = new URL(window.location.href)
    let urlChanged = false

    for (const key of [NO_SIGN_IN_STORAGE_KEY, 'role', NO_SIGN_IN_ROLE_STORAGE_KEY, 'shell', 'workspace']) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key)
        urlChanged = true
      }
    }

    if (urlChanged) {
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
    }
  } catch {
    /* URL manipulation unavailable */
  }
}

export const isMockPreviewActive = (): boolean => false

export const isDebugBypassActive = (): boolean => false

export const isNoSignInPreviewActive = (): boolean => false

export {
  DEBUG_USER,
  DEBUG_PROFILE,
  DEBUG_EDITOR_USER,
  DEBUG_EDITOR_PROFILE,
  DEBUG_VIEWER_USER,
  DEBUG_VIEWER_PROFILE,
  DEBUG_HELPER_USER,
  DEBUG_HELPER_PROFILE,
  DEBUG_HELPER_2_USER,
  DEBUG_HELPER_2_PROFILE,
  DEBUG_HELPER_PHONE_E164,
  DEBUG_HELPER_DISPLAY_NAME,
  DEBUG_ADMIN_COUNTERPARTY,
} from './fixtures'
export { DEBUG_WORKSPACE, DEBUG_WORKSPACE_ID, DEBUG_TEAM_WORKSPACE, DEBUG_TEAM_WORKSPACE_ID } from './workspace'
export {
  getDebugTimelineElements,
  appendDebugTimelineElement,
  updateDebugTimelineElement,
  removeDebugTimelineElement,
  resetDebugTimelineElements,
} from './goals'
export {
  getDebugHelperAssignedItems,
  getDebugHelperMeetings,
  getDebugHelperChannels,
  getDebugHelperChatMessages,
  appendDebugHelperChatMessage,
  subscribeDebugChat,
  type DebugChatMessage,
} from './helperSeed'
export {
  getDebugProofsForItem,
  recordDebugProof,
  recordDebugLocationCheckIn,
  subscribeDebugProofStore,
  getDebugWorkspaceActivity,
  type DebugActivityEntry,
} from './proofStore'
